import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Edit,
  Lock,
  Book,
  Clock,
  CheckCircle2,
  Circle,
  XCircle,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';
import permissionsService from '../../services/permissionsService';
import ReviewWorkflowActions from '../AssessmentBuilder/ReviewWorkflowActions';
import { APPROVE_STUDY_PERMISSION } from '../../services/reviewWorkflowApi';

const convertUTCToLocal = (timeObj) => {
  if (!timeObj || timeObj.period !== 'UTC') return timeObj;
  
  const utcDate = new Date();
  utcDate.setUTCHours(
    parseInt(timeObj.hours, 10),
    parseInt(timeObj.minutes, 10),
    0, 0
  );
  
  let localHours = utcDate.getHours();
  const localPeriod = localHours >= 12 ? 'PM' : 'AM';
  
  localHours = localHours % 12;
  if (localHours === 0) localHours = 12;
  
  return {
    hours: localHours.toString().padStart(2, '0'),
    minutes: utcDate.getMinutes().toString().padStart(2, '0'),
    period: localPeriod
  };
};

// Render a stored window time so it's never ambiguous which timezone it means.
// Stored times are UTC; show the viewer's local time WITH a timezone label, then
// the canonical UTC value, e.g. "08:00 PM EDT · 00:00 UTC".
const formatWindowTime = (timeObj) => {
  if (!timeObj) return '—';
  if (timeObj.period !== 'UTC') {
    // Already a wall-clock local value (no UTC conversion to apply); show as-is.
    return `${timeObj.hours}:${timeObj.minutes} ${timeObj.period || ''}`.trim();
  }
  const local = convertUTCToLocal(timeObj);
  let tz = 'local';
  try {
    tz = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value || 'local';
  } catch { /* keep the 'local' fallback */ }
  const utc = `${String(timeObj.hours).padStart(2, '0')}:${String(timeObj.minutes).padStart(2, '0')}`;
  return `${local.hours}:${local.minutes} ${local.period} ${tz} · ${utc} UTC`;
};

// -- CHILD COMPONENT: DraftDetails --
export function DraftDetails({
  draft,
  isLoading,
  surveysForDraft,
  remindersForDraft
}) {
  const getSurveyNamesByIds = useCallback(
    (ids = []) => {
      if (!Array.isArray(surveysForDraft)) {
        return ids.map(idStr => `Survey #${idStr}`);
      }
      return ids.map((idStr) => {
        const found = surveysForDraft.find((s) => s.id === parseInt(idStr, 10));
        return found ? found.name : `Survey #${idStr}`;
      });
    },
    [surveysForDraft]
  );

  const getReminderNamesByIds = useCallback(
    (ids = []) => {
      if (!Array.isArray(remindersForDraft)) {
        console.log('remindersForDraft is not an array:', remindersForDraft);
        return ids.map(idStr => `Reminder #${idStr}`);
      }
      
      console.log('Looking up reminder IDs:', ids, 'in reminders:', remindersForDraft);
      
      return ids.map((idStr) => {
        // Convert string ID to number for comparison
        const idNum = parseInt(idStr, 10);
        
        const found = remindersForDraft.find((r) => r.id === idNum);
        return found ? found.name : `Reminder #${idStr}`;
      });
    },
    [remindersForDraft]
  );

  // Reusable Section Components
  const SectionTitle = ({ children }) => (
    <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{children}</h5>
  );

  const EmptyState = ({ message }) => (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
      <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center">{message}</p>
    </div>
  );

  const ListItem = ({ children }) => (
    <li className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
      <span>{children}</span>
    </li>
  );

  const ScheduleItem = ({ schedule }) => {
    const [open, setOpen] = useState(false);
    
    const standardSurveyNames = getSurveyNamesByIds(schedule.standardSurveys || []);
    const missingFormNames = getSurveyNamesByIds(schedule.missingForms || []);
    const pushReminderNames = getReminderNamesByIds(schedule.pushReminders || []);
    const emailReminderNames = getReminderNamesByIds(schedule.emailReminders || []);

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 transition-all duration-200 hover:shadow-md dark:hover:shadow-gray-900/20">
        <button
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between p-4 group transition-colors duration-200"
        >
          <div className="flex items-center space-x-3">
            <div
              className={`p-2 rounded-full transition-colors duration-200 ${
                open ? 'bg-primary-50' : 'bg-gray-50 dark:bg-gray-900 group-hover:bg-primary-50'
              }`}
            >
              <Clock
                className={`w-4 h-4 transition-colors duration-200 ${
                  open ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500 group-hover:text-primary-600'
                }`}
              />
            </div>
            <div className="text-left">
              <span className="font-medium text-gray-900 dark:text-gray-100 block">{schedule.name}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{schedule.type}</span>
            </div>
          </div>
          <div className={`transition-transform duration-200 ${open ? '-rotate-180' : ''}`}>
            <ChevronDown
              className={`w-5 h-5 transition-colors duration-200 ${
                open ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500 group-hover:text-primary-600'
              }`}
            />
          </div>
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden border-t border-gray-100 dark:border-gray-700"
            >
              <div className="p-4 space-y-6 bg-gray-50 dark:bg-gray-900">
                {/* Arms */}
                <div>
                  <SectionTitle>Schedule Arms</SectionTitle>
                  {Array.isArray(schedule.arms) && schedule.arms.length > 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <ul className="space-y-2">
                        {schedule.arms.map((arm, idx) => (
                          <ListItem key={idx}>{arm}</ListItem>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <EmptyState message="No arms selected" />
                  )}
                </div>

                {/* Standard Surveys */}
                <div>
                  <SectionTitle>Standard Surveys</SectionTitle>
                  {standardSurveyNames.length > 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <ul className="space-y-2">
                        {standardSurveyNames.map((name, idx) => (
                          <ListItem key={idx}>{name}</ListItem>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <EmptyState message="No standard surveys configured" />
                  )}
                </div>

                {/* Missing Forms */}
                <div>
                  <SectionTitle>Missing Forms</SectionTitle>
                  {schedule.enableMissingForms && missingFormNames.length > 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <ul className="space-y-2">
                        {missingFormNames.map((name, idx) => (
                          <ListItem key={idx}>{name}</ListItem>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <EmptyState message="Missing Forms not enabled for this schedule" />
                  )}
                </div>

                {/* Push Reminders */}
                <div>
                  <SectionTitle>Push Reminders</SectionTitle>
                  {schedule.enablePushReminders && pushReminderNames.length > 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <ul className="space-y-2">
                        {pushReminderNames.map((name, idx) => (
                          <ListItem key={idx}>{name}</ListItem>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <EmptyState message="Push Reminders not enabled" />
                  )}
                </div>

                {/* Email Reminders */}
                <div>
                  <SectionTitle>Email Reminders</SectionTitle>
                  {schedule.enableEmailReminders && emailReminderNames.length > 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <ul className="space-y-2">
                        {emailReminderNames.map((name, idx) => (
                          <ListItem key={idx}>{name}</ListItem>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <EmptyState message="Email Reminders not enabled" />
                  )}
                </div>

                {/* Site Type Section */}
                <div>
                  <SectionTitle>Site Type</SectionTitle>
                  {schedule.siteType ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {schedule.siteType === 'on-site' ? 'On-site' : 'Remote'}
                      </p>
                    </div>
                  ) : (
                    <EmptyState message="No site type configured" />
                  )}
                </div>

                {/* Assessment Windows */}
                <div>
                  <SectionTitle>Assessment Windows</SectionTitle>
                  {(() => {
                    // Normalize legacy single-window shape into an array
                    const windows = Array.isArray(schedule.assessmentWindows)
                      ? schedule.assessmentWindows
                      : schedule.assessmentWindow
                        ? [schedule.assessmentWindow]
                        : [];
                    if (!schedule.hasAssessmentWindow || windows.length === 0) {
                      return <EmptyState message="No assessment window configured" />;
                    }
                    return (
                      <div className="space-y-3">
                        {windows.map((win, idx) => (
                          <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-4 space-y-3">
                            {win.name && (
                              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {win.name}
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Open Day Offset:</span>
                                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                                  {win.openWindowDay}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Close Day Offset:</span>
                                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                                  {win.closeWindowDay}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Start Time:</span>
                                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                                  {formatWindowTime(win.startTime)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">End Time:</span>
                                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                                  {formatWindowTime(win.endTime)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Stages */}
                <div>
                  <SectionTitle>Stages</SectionTitle>
                  {Array.isArray(schedule.stages) && schedule.stages.length > 0 ? (
                    <div className="space-y-2">
                      {schedule.stages.map((stg, idx) => (
                        <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center text-sm font-medium">
                              {idx + 1}
                            </div>
                            <div>
                              <span className="font-medium text-gray-900 dark:text-gray-100">{stg.name}</span>
                              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Days {stg.startDay} - {stg.endDay}, repeats every{' '}
                                {stg.frequency.value} {stg.frequency.unit}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState message="No stages defined" />
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const data = draft.data || {};
  
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ type: 'spring', duration: 0.4, bounce: 0.1 }}
      className="overflow-hidden bg-gray-50 dark:bg-gray-900"
    >
      <div className="px-6 py-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent mb-3"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading design details...</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Review status */}
            {(() => {
              const rs = draft.review_status || 'draft';
              const meta = ({
                draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-700', Icon: Edit },
                in_review: { label: 'Under Review', cls: 'bg-blue-100 text-blue-800', Icon: Clock },
                approved: { label: 'Approved', cls: 'bg-teal-100 text-teal-800', Icon: CheckCircle2 },
                denied: { label: 'Changes Requested', cls: 'bg-red-100 text-red-800', Icon: XCircle },
                published: { label: 'Published', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
              })[rs] || { label: rs, cls: 'bg-gray-100 text-gray-700', Icon: Clock };
              const Icon = meta.Icon;
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Review status</h4>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ${meta.cls}`}>
                      <Icon className="w-3.5 h-3.5" /> {meta.label}
                    </span>
                  </div>
                  {draft.submitted_at && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {rs === 'in_review' ? 'Submitted' : 'Last submitted'} {new Date(draft.submitted_at).toLocaleString()}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Readiness checklist */}
            {(() => {
              const items = [
                { label: 'Study start day set', done: data.schedules?.[0]?.studyStartDay !== undefined },
                { label: 'Arms configured', done: Array.isArray(data.arms) && data.arms.length > 0 },
                { label: 'Schedules configured', done: Array.isArray(data.schedules) && data.schedules.length > 0 },
                { label: 'Surveys assigned', done: Array.isArray(surveysForDraft) && surveysForDraft.length > 0 },
              ];
              const doneCount = items.filter((i) => i.done).length;
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Readiness</h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{doneCount}/{items.length} complete</span>
                  </div>
                  <ul className="space-y-2">
                    {items.map((it, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        {it.done
                          ? <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0" />
                          : <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />}
                        <span className={it.done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}>{it.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Design configuration — flat, single level (no nested chevron) */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 p-4 space-y-6">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Design Configuration</h4>

              {/* Study Start Day */}
              <div>
                <SectionTitle>Study Start Day</SectionTitle>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {data.schedules && data.schedules[0]?.studyStartDay !== undefined
                      ? `Day ${data.schedules[0].studyStartDay}`
                      : 'Not configured'}
                  </p>
                </div>
              </div>

              {/* Arms */}
              <div>
                <SectionTitle>Available Arms</SectionTitle>
                {Array.isArray(data.arms) && data.arms.length > 0 ? (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <ul className="space-y-2">
                      {data.arms.map((arm, idx) => (
                        <ListItem key={idx}>{arm}</ListItem>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <EmptyState message="No arms configured" />
                )}
              </div>

              {/* Schedules */}
              <div>
                <SectionTitle>Schedules</SectionTitle>
                {Array.isArray(data.schedules) && data.schedules.length > 0 ? (
                  <div className="space-y-3">
                    {data.schedules.map((schedule, i) => (
                      <ScheduleItem key={i} schedule={schedule} />
                    ))}
                  </div>
                ) : (
                  <EmptyState message="No schedules configured" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// -- PARENT COMPONENT: StudyList --
// Logged-in user id, used to scope review-workflow actions (submitter-only).
const getCurrentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.id ?? u?.user_id ?? u?.pk ?? null;
  } catch {
    return null;
  }
};

// Review-state badge styling for study drafts (separate axis from `status`).
const REVIEW_BADGE = {
  in_review: { label: 'Under Review', cls: 'bg-blue-100 text-blue-800' },
  approved: { label: 'Approved', cls: 'bg-teal-100 text-teal-800' },
  denied: { label: 'Changes Requested', cls: 'bg-red-100 text-red-800' },
  published: { label: 'Published', cls: 'bg-emerald-100 text-emerald-800' },
};

function StudyList() {
  const navigate = useNavigate();
  const currentUserId = getCurrentUserId();
  const [canApproveStudy, setCanApproveStudy] = useState(false);
  const [studyDrafts, setStudyDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDraft, setExpandedDraft] = useState(null);
  const [surveys, setSurveys] = useState({});
  const [reminderTemplates, setReminderTemplates] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });

  // silent=true refreshes the list in the background (no spinner, no error
  // toast) so polling/focus refetches don't flicker the table or interrupt.
  const loadStudyDrafts = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await studyBuilderService.getStudyDrafts();
      setStudyDrafts(response || []);
    } catch (error) {
      console.error('Error loading drafts:', error);
      if (!silent) toast.error('Failed to load drafts');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStudyDrafts();
  }, [loadStudyDrafts]);

  // Keep the list roughly live across users: silent refetch every 30s (matching
  // the notification bell's cadence) and whenever the tab regains focus, so a
  // submission/approval on another machine shows up without a manual refresh.
  useEffect(() => {
    const tick = () => loadStudyDrafts(true);
    const interval = setInterval(tick, 30000);
    const onFocus = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadStudyDrafts]);

  // Whether the current user can approve study drafts — enables inline approve/deny.
  useEffect(() => {
    let active = true;
    permissionsService
      .hasPermission(APPROVE_STUDY_PERMISSION)
      .then((ok) => { if (active) setCanApproveStudy(!!ok); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const handleDraftExpand = useCallback((draftId) => {
    if (expandedDraft === draftId) {
      setExpandedDraft(null);
      return;
    }
    setExpandedDraft(draftId);

    const draft = studyDrafts.find((d) => d.id === draftId);
    if (!draft) return;

    setLoadingDetails((prev) => ({ ...prev, [draftId]: true }));
    Promise.all([
      studyBuilderService.getSurveysByStudyName(draft.name),
      studyBuilderService.getReminderTemplates(draft.id),
    ])
      .then(([surveyData, templateData]) => {
        setSurveys((prev) => ({
          ...prev,
          [draft.id]: surveyData?.surveys || [],
        }));
        
        // Ensure templateData is properly processed as an array from paginated response
        const reminderTemplatesArray = templateData?.results || templateData || [];
        console.log('Reminder templates data:', reminderTemplatesArray);
        
        setReminderTemplates((prev) => ({
          ...prev,
          [draft.id]: reminderTemplatesArray,
        }));
      })
      .catch((err) => {
        console.error('Error loading details for draft', draftId, err);
        toast.error('Failed to load draft details');
      })
      .finally(() => {
        setLoadingDetails((prev) => ({ ...prev, [draftId]: false }));
      });
  }, [expandedDraft, studyDrafts]);

  const handleEditClick = useCallback((draft) => {
    localStorage.removeItem('studyDrafts');
    setSurveys({});
    setReminderTemplates({});
    localStorage.setItem('currentStudyId', String(draft.id));
    localStorage.setItem('currentStudyDraftId', String(draft.id));

    studyBuilderService.getStudyDrafts().then((resp) => {
      setStudyDrafts(resp);
    });

    navigate(`/studybuilder/${encodeURIComponent(draft.name)}`);
  }, [navigate]);

  // Open a submitted draft in review mode: same builder load as editing, but
  // lands on the review route which pins the Approve/Deny bar to the bottom.
  const handleReviewClick = useCallback((draft) => {
    localStorage.removeItem('studyDrafts');
    setSurveys({});
    setReminderTemplates({});
    localStorage.setItem('currentStudyId', String(draft.id));
    localStorage.setItem('currentStudyDraftId', String(draft.id));
    navigate(`/studybuilder/review/${encodeURIComponent(draft.name)}`, {
      state: { reviewEntity: draft },
    });
  }, [navigate]);

  const formatDate = useMemo(() => (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Format status based on backend DRAFT_STATUS_CHOICES mapping
const getStatusDisplay = useMemo(() => (status) => {
    const statusMap = {
      'incomplete': 'Incomplete',
      'in_progress': 'In Progress',
      'ready_for_review': 'Ready for Review',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'archived': 'Archived'
    };
    return statusMap[status] || status;
  }, []);

const getStatusColor = useMemo(() => (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const s = status.toLowerCase();
    if (s === 'incomplete') return 'bg-yellow-100 text-yellow-800';
    if (s === 'in_progress') return 'bg-blue-100 text-blue-800';
    if (s === 'ready_for_review') return 'bg-green-100 text-green-800';
    if (s === 'approved') return 'bg-emerald-100 text-emerald-800';
    if (s === 'rejected') return 'bg-red-100 text-red-800';
    if (s === 'archived') return 'bg-gray-100 text-gray-600';
    return 'bg-gray-100 text-gray-800';
  }, []);

  const handleSort = useCallback((key) => {
    setSortConfig((prevConfig) => {
      // If clicking the same column, toggle direction
      if (prevConfig.key === key) {
        return { 
          ...prevConfig, 
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' 
        };
      }
      // If clicking a new column, default to ascending
      return { key, direction: 'asc' };
    });
  }, []);

  const sortedStudyDrafts = useMemo(() => {
    // Ensure studyDrafts is an array
    const draftsArray = Array.isArray(studyDrafts) ? studyDrafts : 
                       (studyDrafts && typeof studyDrafts === 'object' ? Object.values(studyDrafts) : []);
    
    console.log('Draft array after conversion:', draftsArray);
    const draftsToSort = [...draftsArray];
    console.log('Drafts to sort:', draftsToSort);
    if (sortConfig.key) {
      draftsToSort.sort((a, b) => {
        // First check if a or b are null/undefined
        if (!a) return 1;
        if (!b) return -1;
        
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle special cases (null, undefined, formatting)
        if (sortConfig.key === 'status') {
          aValue = getStatusDisplay(aValue) || '';
          bValue = getStatusDisplay(bValue) || '';
        }
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // String comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Numeric or date comparison
        try {
          return sortConfig.direction === 'asc' 
            ? (aValue > bValue ? 1 : -1) 
            : (bValue > aValue ? 1 : -1);
        } catch (error) {
          console.error('Error during sorting comparison:', error);
          return 0; // Return equal if comparison fails
        }
      });
    }
    return draftsToSort;
  }, [studyDrafts, sortConfig, getStatusDisplay]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/studybuilder')}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Study Builder</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Study Drafts</h1>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent"></div>
          </div>
        ) : studyDrafts.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900/20 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      <span>Study Name</span>
                      <div className="ml-1">
                        {sortConfig.key === 'name' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-primary-600" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-primary-600" />
                          )
                        ) : (
                          <div className="w-3 h-3 opacity-0 group-hover:opacity-40">
                            <ArrowUp className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      <span>Status</span>
                      <div className="ml-1">
                        {sortConfig.key === 'status' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-primary-600" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-primary-600" />
                          )
                        ) : (
                          <div className="w-3 h-3 opacity-0 group-hover:opacity-40">
                            <ArrowUp className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex items-center">
                      <span>Last Updated</span>
                      <div className="ml-1">
                        {sortConfig.key === 'updated_at' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-primary-600" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-primary-600" />
                          )
                        ) : (
                          <div className="w-3 h-3 opacity-0 group-hover:opacity-40">
                            <ArrowUp className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedStudyDrafts.length > 0 ? (
                  sortedStudyDrafts
                    .filter(draft => {
                      console.log('Checking draft for rendering:', draft);
                      return draft && typeof draft === 'object';
                    })
                    .map((draft) => (
                      <React.Fragment key={draft?.id || Math.random()}>
                        <tr className="group hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="p-2 rounded-full bg-gray-50 dark:bg-gray-900 group-hover:bg-white dark:group-hover:bg-gray-800 transition-colors duration-200">
                                <Book className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                              </div>
                              <div className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">{draft.name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-3 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusColor(
                                  draft.status
                                )}`}
                              >
                                {getStatusDisplay(draft.status) || 'Unknown'}
                              </span>
                              {draft.review_status && REVIEW_BADGE[draft.review_status] && (
                                <span
                                  className={`px-3 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${REVIEW_BADGE[draft.review_status].cls}`}
                                >
                                  {REVIEW_BADGE[draft.review_status].label}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                              <Clock className="w-4 h-4 mr-1.5" />
                              {formatDate(draft.updated_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-4">
                              <ReviewWorkflowActions
                                entity={draft}
                                entityType="study"
                                currentUserId={currentUserId}
                                isApprover={canApproveStudy}
                                onChanged={loadStudyDrafts}
                                onReview={handleReviewClick}
                              />
                              {['in_review', 'approved'].includes(draft.review_status) ? (
                                <span
                                  className="flex items-center text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                  title="Locked while under review — recall it to edit"
                                >
                                  <div className="p-1.5 rounded-full">
                                    <Lock className="w-4 h-4" />
                                  </div>
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleEditClick(draft)}
                                  className="group/edit flex items-center text-gray-400 dark:text-gray-500 hover:text-primary-600 transition-colors duration-200"
                                  title="Edit Draft"
                                >
                                  <div className="p-1.5 rounded-full group-hover/edit:bg-primary-50 transition-colors duration-200">
                                    <Edit className="w-4 h-4" />
                                  </div>
                                </button>
                              )}
                              <button
                                onClick={() => handleDraftExpand(draft.id)}
                                className="group/expand flex items-center text-gray-400 dark:text-gray-500 hover:text-primary-600 transition-colors duration-200"
                              >
                                <div className="p-1.5 rounded-full group-hover/expand:bg-primary-50 transition-colors duration-200">
                                  {expandedDraft === draft.id ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedDraft === draft.id && (
                          <tr>
                            <td colSpan={4} className="p-0">
                              <AnimatePresence>
                                <DraftDetails
                                  draft={draft}
                                  isLoading={loadingDetails[draft.id]}
                                  surveysForDraft={surveys[draft.id] || []}
                                  remindersForDraft={reminderTemplates[draft.id] || []}
                                />
                              </AnimatePresence>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20">
                      <div className="p-3 rounded-full bg-gray-50 dark:bg-gray-900 w-fit mx-auto mb-4">
                        <Book className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No study drafts found</h3>
                      <p className="text-gray-500 dark:text-gray-400">Create your first study to get started</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20">
            <div className="p-3 rounded-full bg-gray-50 dark:bg-gray-900 w-fit mx-auto mb-4">
              <Book className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No study drafts found</h3>
            <p className="text-gray-500 dark:text-gray-400">Create your first study to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudyList;